import React, { useState, useEffect } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { X, ChevronRight, User, DollarSign, Percent, Calendar, Briefcase } from 'lucide-react';
import Button from '../ui/Button';
import Input from '../ui/Input';
import Select from '../ui/Select';

export interface EmployeeFormData {
  name: string;
  email?: string | null;
  role: string;
  base_salary: number;
  commission_percent?: number;
  salary_review_frequency_months: number;
  last_salary_review_at?: string | null;
  vacation_days_per_year: number;
  is_active: boolean;
  is_independent?: boolean;
}

interface EmployeeModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: EmployeeFormData) => Promise<void>;
  initialData?: EmployeeFormData & { user_id?: string | null };
}

type WizardStep = 1 | 2;

const ROLES = [
  { value: 'Producción', label: 'Producción' },
  { value: 'Depósito', label: 'Depósito' },
  { value: 'Otro', label: 'Otro' },
];

const EmployeeModal: React.FC<EmployeeModalProps> = ({ isOpen, onClose, onSave, initialData }) => {
  const [currentStep, setCurrentStep] = useState<WizardStep>(1);
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState<EmployeeFormData>({
    name: '',
    role: 'Otro',
    base_salary: 0,
    salary_review_frequency_months: 3,
    vacation_days_per_year: 14,
    is_active: true,
    is_independent: false,
  });

  const isVendedor = !!initialData?.user_id;

  useEffect(() => {
    if (initialData) {
      const lastReview = initialData.last_salary_review_at ? initialData.last_salary_review_at.slice(0, 7) : '';
      setFormData({
        name: initialData.name || '',
        email: initialData.email ?? '',
        role: initialData.role || 'Otro',
        base_salary: initialData.base_salary ?? 0,
        commission_percent: initialData.commission_percent ?? 0,
        salary_review_frequency_months: initialData.salary_review_frequency_months ?? 3,
        last_salary_review_at: lastReview || null,
        vacation_days_per_year: initialData.vacation_days_per_year ?? 14,
        is_active: initialData.is_active ?? true,
        is_independent: initialData.is_independent ?? false,
      });
    } else {
      setFormData({
        name: '',
        role: 'Otro',
        base_salary: 0,
        salary_review_frequency_months: 3,
        vacation_days_per_year: 14,
        is_active: true,
        is_independent: false,
      });
    }
  }, [initialData, isOpen]);

  const step1Valid = !!formData.name?.trim();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!step1Valid) return;
    setIsLoading(true);
    try {
      await onSave(formData);
      onClose();
    } catch (e) {
      console.error(e);
    } finally {
      setIsLoading(false);
    }
  };

  if (initialData) {
    return (
      <Dialog.Root open={isOpen} onOpenChange={(open) => !open && onClose()}>
        <Dialog.Portal>
          <Dialog.Overlay className="fixed inset-0 z-[100] bg-black/50" />
          <Dialog.Content
            className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-[100] w-full max-w-lg rounded-2xl bg-white dark:bg-gray-800 shadow-xl max-h-[90vh] overflow-y-auto animate-fade-in"
            onInteractOutside={(e) => {
              const t = e.target as HTMLElement;
              if (t.closest('[data-radix-select-viewport], [role="listbox"], [role="option"], [data-radix-popper-content-wrapper]')) e.preventDefault();
            }}
          >
            <div className="px-6 pt-6 pb-6">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-xl bg-indigo-100 dark:bg-indigo-900/40">
                    <User className="h-6 w-6 text-indigo-600 dark:text-indigo-400" />
                  </div>
                  <div>
                    <Dialog.Title asChild>
                      <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Editar colaborador</h2>
                    </Dialog.Title>
                    <p className="text-sm text-gray-500 dark:text-gray-400">{initialData.name}</p>
                  </div>
                </div>
                <button type="button" onClick={onClose} className="p-2 -m-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500">
                  <X className="h-5 w-5" />
                </button>
              </div>
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="p-4 rounded-2xl bg-gray-50 dark:bg-gray-900/50 border-2 border-gray-100 dark:border-gray-700">
                  <p className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3 flex items-center gap-2">
                    <User className="h-4 w-4 text-indigo-500" />
                    Datos personales
                  </p>
                  <div className="space-y-3">
                    <Input label="Nombre" value={formData.name || ''} onChange={(e) => setFormData((p) => ({ ...p, name: e.target.value }))} placeholder="Nombre completo" />
                    {isVendedor && <Input label="Email" value={formData.email || ''} disabled placeholder="email@productex.com" />}
                  </div>
                </div>
                <div className="p-4 rounded-2xl bg-indigo-50/50 dark:bg-indigo-900/10 border-2 border-indigo-100 dark:border-indigo-800/50">
                  <p className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3 flex items-center gap-2">
                    <DollarSign className="h-4 w-4 text-indigo-500" />
                    Remuneración
                  </p>
                  <div className="space-y-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Sueldo base</label>
                      <div className="relative">
                        <span className="absolute left-4 top-1/2 -translate-y-1/2 text-lg font-semibold text-gray-400">$</span>
                        <input
                        type="number"
                        value={formData.base_salary != null ? String(formData.base_salary) : ''}
                        onChange={(e) => setFormData((p) => ({ ...p, base_salary: parseFloat(e.target.value) || 0 }))}
                        placeholder="0"
                        min="0"
                        step="0.01"
                        className="w-full h-12 pl-8 pr-4 rounded-xl border-2 border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 text-lg font-semibold"
                        />
                      </div>
                    </div>
                    {isVendedor && <Input label="% Comisión" type="number" min="0" max="100" step="0.5" value={formData.commission_percent != null ? String(formData.commission_percent) : ''} onChange={(e) => setFormData((p) => ({ ...p, commission_percent: parseFloat(e.target.value) || 0 }))} placeholder="0" />}
                  </div>
                </div>
                <div className="p-4 rounded-2xl bg-gray-50 dark:bg-gray-900/50 border-2 border-gray-100 dark:border-gray-700">
                  <p className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3 flex items-center gap-2">
                    <Calendar className="h-4 w-4 text-indigo-500" />
                    Configuración
                  </p>
                  <div className="grid grid-cols-2 gap-4">
                    <Input label="Revisión sueldo (meses)" type="number" min="1" value={formData.salary_review_frequency_months != null ? String(formData.salary_review_frequency_months) : '3'} onChange={(e) => setFormData((p) => ({ ...p, salary_review_frequency_months: parseInt(e.target.value, 10) || 3 }))} />
                    <Input label="Días vacaciones/año" type="number" min="0" value={formData.vacation_days_per_year != null ? String(formData.vacation_days_per_year) : '14'} onChange={(e) => setFormData((p) => ({ ...p, vacation_days_per_year: parseInt(e.target.value, 10) || 14 }))} />
                  </div>
                  <div className="mt-3">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Mes base para revisión de sueldo</label>
                    <input
                      type="month"
                      value={formData.last_salary_review_at || ''}
                      onChange={(e) => setFormData((p) => ({ ...p, last_salary_review_at: e.target.value || null }))}
                      className="w-full h-10 px-3 rounded-xl border-2 border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                    />
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Período desde el cual se cuenta la próxima actualización de sueldo</p>
                  </div>
                  <label className="flex items-center gap-2 cursor-pointer mt-4 p-3 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors">
                    <input type="checkbox" checked={formData.is_active ?? true} onChange={(e) => setFormData((p) => ({ ...p, is_active: e.target.checked }))} className="rounded border-gray-300 w-4 h-4" />
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Colaborador activo</span>
                  </label>
                </div>
                <div className="flex gap-3 pt-2">
                  <Button type="button" variant="outline" onClick={onClose} className="flex-1 rounded-xl">Cancelar</Button>
                  <Button type="submit" disabled={isLoading} className="flex-1 rounded-xl">{isLoading ? 'Guardando...' : 'Guardar cambios'}</Button>
                </div>
              </form>
            </div>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>
    );
  }

  return (
    <Dialog.Root open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-[100] bg-black/50" />
        <Dialog.Content
          className="fixed inset-0 z-[100] flex items-center justify-center p-4"
          onInteractOutside={(e) => {
            const t = e.target as HTMLElement;
            if (t.closest('[data-radix-select-viewport], [role="listbox"], [role="option"], [data-radix-popper-content-wrapper]')) e.preventDefault();
          }}
        >
          <div className="w-full max-w-md bg-white dark:bg-gray-800 rounded-2xl shadow-xl overflow-hidden animate-fade-in">
            <div className="px-6 pt-6 pb-4">
              <div className="flex items-center justify-between mb-4">
                <Dialog.Title asChild>
                  <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Nuevo colaborador</h2>
                </Dialog.Title>
                <button type="button" onClick={onClose} className="p-2 -m-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500">
                  <X className="h-5 w-5" />
                </button>
              </div>
              <div className="flex gap-2">
                <div className={`flex-1 h-1.5 rounded-full ${currentStep >= 1 ? 'bg-indigo-500' : 'bg-gray-200 dark:bg-gray-700'}`} />
                <div className={`flex-1 h-1.5 rounded-full ${currentStep >= 2 ? 'bg-indigo-500' : 'bg-gray-200 dark:bg-gray-700'}`} />
              </div>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">Paso {currentStep} de 2</p>
            </div>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                if (currentStep === 1 && step1Valid) { setCurrentStep(2); return; }
                if (currentStep === 2) handleSubmit(e);
              }}
              className="px-6 pb-6"
            >
              {currentStep === 1 && (
                <div className="space-y-5">
                  <div>
                    <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 flex items-center gap-2">
                      <User className="h-4 w-4 text-indigo-500" />
                      ¿Quién es?
                    </p>
                    <Input label="Nombre" value={formData.name || ''} onChange={(e) => setFormData((p) => ({ ...p, name: e.target.value }))} placeholder="Nombre completo" />
                    <Input label="Email (opcional)" type="email" value={formData.email || ''} onChange={(e) => setFormData((p) => ({ ...p, email: e.target.value }))} placeholder="opcional" className="mt-3" />
                    <div className="mt-3">
                      <Select label="Rol" value={formData.role || 'Otro'} onChange={(v) => setFormData((p) => ({ ...p, role: v }))} options={ROLES} />
                    </div>
                    <label className="flex items-center gap-2 cursor-pointer mt-3">
                      <input type="checkbox" checked={formData.is_independent ?? false} onChange={(e) => setFormData((p) => ({ ...p, is_independent: e.target.checked }))} className="rounded border-gray-300" />
                      <span className="text-sm text-gray-700 dark:text-gray-300">Independiente</span>
                    </label>
                  </div>
                </div>
              )}
              {currentStep === 2 && (
                <div className="space-y-5">
                  <div>
                    <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 flex items-center gap-2">
                      <DollarSign className="h-4 w-4 text-indigo-500" />
                      Sueldo base
                    </p>
                    <div className="relative">
                      <span className="absolute left-5 top-1/2 -translate-y-1/2 text-2xl font-semibold text-gray-400 dark:text-gray-500">$</span>
                      <input
                        type="number"
                        value={formData.base_salary != null ? String(formData.base_salary) : ''}
                        onChange={(e) => setFormData((p) => ({ ...p, base_salary: parseFloat(e.target.value) || 0 }))}
                        placeholder="0"
                        min="0"
                        step="0.01"
                        className="w-full h-16 pl-12 pr-5 rounded-2xl border-2 border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-900 text-2xl font-bold text-gray-900 dark:text-white focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 transition-all"
                      />
                    </div>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 flex items-center gap-2">
                      <Calendar className="h-4 w-4 text-indigo-500" />
                      Revisión y vacaciones
                    </p>
                    <div className="grid grid-cols-2 gap-4">
                      <Input label="Revisión sueldo (meses)" type="number" min="1" value={formData.salary_review_frequency_months != null ? String(formData.salary_review_frequency_months) : '3'} onChange={(e) => setFormData((p) => ({ ...p, salary_review_frequency_months: parseInt(e.target.value, 10) || 3 }))} />
                      <Input label="Días vacaciones/año" type="number" min="0" value={formData.vacation_days_per_year != null ? String(formData.vacation_days_per_year) : '14'} onChange={(e) => setFormData((p) => ({ ...p, vacation_days_per_year: parseInt(e.target.value, 10) || 14 }))} />
                    </div>
                  </div>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" checked={formData.is_active ?? true} onChange={(e) => setFormData((p) => ({ ...p, is_active: e.target.checked }))} className="rounded border-gray-300" />
                    <span className="text-sm text-gray-700 dark:text-gray-300">Activo</span>
                  </label>
                </div>
              )}
              <div className="flex gap-3 mt-6">
                {currentStep === 1 ? (
                  <>
                    <Button type="button" variant="outline" onClick={onClose} className="flex-1">Cancelar</Button>
                    <button type="submit" disabled={!step1Valid} className="flex-1 flex items-center justify-center gap-1 px-4 py-2 rounded-md border border-transparent bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium">
                      Siguiente <ChevronRight className="h-4 w-4" />
                    </button>
                  </>
                ) : (
                  <>
                    <Button type="button" variant="outline" onClick={() => setCurrentStep(1)} className="flex-1">Atrás</Button>
                    <Button type="submit" disabled={isLoading} className="flex-1">{isLoading ? 'Guardando...' : 'Crear colaborador'}</Button>
                  </>
                )}
              </div>
            </form>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};

export default EmployeeModal;
